import axios from '@/api/utils/axios'
import {
  normalizeSchemeDialogueMessage,
  normalizeSchemeDialogueSession,
  normalizeSnowflakeId
} from '../adapters/index.js'

const baseUrl = '/longstron/applet/schemeEco/chat'

/**
 * axios 先将响应体 JSON.parse，超大雪花 Long 会变成 number 丢精度。
 * 在解析前把原始字符串里的长数字用户 ID 改成 JSON 字符串，避免会话/消息侧「永远对不齐当前用户」。
 */
function schemeEcoJsonParseConfig(extraConfig = {}) {
  return {
    ...extraConfig,
    transformResponse: [
      function schemeEcoPreserveSnowflakeStrings(data) {
        if (typeof data !== 'string') return data
        try {
          let patched = data
          const keys = [
            'senderUserId',
            'receiverUserId',
            'fromUserId',
            'toUserId',
            'conversationId'
          ]
          for (const key of keys) {
            patched = patched.replace(
              new RegExp(`"${key}"\\s*:\\s*(\\d{15,})\\b`, 'g'),
              `"${key}":"$1"`
            )
          }
          patched = patched.replace(/"id"\s*:\s*(\d{15,})\b/g, '"id":"$1"')
          return JSON.parse(patched)
        } catch (e) {
          try {
            return JSON.parse(data)
          } catch (e2) {
            return {}
          }
        }
      }
    ]
  }
}

export async function fetchSchemeConversationList() {
  const res = await axios.get(baseUrl + '/conversations', schemeEcoJsonParseConfig())
  if (res?.code !== '00000') return []
  const rows = Array.isArray(res.model) ? res.model : []
  return rows.map((x) => ({
    id: normalizeSnowflakeId(x.id) || String(x.id || ''),
    conversationId: normalizeSnowflakeId(x.id) || String(x.id || ''),
    chatKind: x.bizType || 'customer',
    entityId: x.bizEntityId || '',
    name: x.title || '未命名会话',
    subtitle: x.subtitle || '',
    senderUserId: normalizeSnowflakeId(x.senderUserId || x.fromUserId || ''),
    senderName: x.senderName || x.fromUserName || '',
    receiverUserId: normalizeSnowflakeId(x.receiverUserId || x.toUserId || ''),
    receiverName: x.receiverName || x.toUserName || x.title || '',
    sendType: x.sendType || x.send_type || '',
    receiveType: x.receiveType || x.receive_type || '',
    unreadCount: x.unreadCount || 0,
    lastMessagePreview: x.lastMessagePreview || '',
    lastMessageAt: x.lastMessageAt || '',
    messages: [],
    hasMoreHistory: true,
    loadingHistory: false
  }))
}

export async function createOrGetSchemeConversation(payload = {}) {
  const res = await axios.post(
    baseUrl + '/conversations',
    {
      bizType: payload.bizType || 'customer',
      bizEntityId: payload.bizEntityId || '',
      title: payload.title || '',
      subtitle: payload.subtitle || '',
      senderUserId: payload.senderUserId || '',
      senderName: payload.senderName || '',
      receiverUserId: payload.receiverUserId || '',
      receiverName: payload.receiverName || '',
      sendType: payload.sendType || '',
      receiveType: payload.receiveType || ''
    },
    schemeEcoJsonParseConfig()
  )
  if (res?.code !== '00000') return null
  return res.model || null
}

export async function fetchSchemeDialogueMessagesByConversation(
  conversationId,
  raw = false,
  limit = 60,
  beforeSeq = null,
  afterSeq = null,
  options = {}
) {
  if (!conversationId) return []
  const res = await axios.get(
    baseUrl + `/conversations/${conversationId}/messages`,
    schemeEcoJsonParseConfig({
      params: {
        limit,
        beforeSeq: beforeSeq || undefined,
        afterSeq: afterSeq || undefined,
        ...(options && options.silent ? { loading: true } : null)
      }
    })
  )
  if (res?.code !== '00000') return []
  const rows = Array.isArray(res.model) ? res.model : []
  const currentUserId = options && options.currentUserId ? String(options.currentUserId) : ''
  return raw ? rows : rows.map((x) => normalizeSchemeDialogueMessage(x, currentUserId)).filter(Boolean)
}

export async function createSchemeDialogueMessage(payload = {}, options = {}) {
  let conversationId = payload.conversationId || ''
  if (!conversationId && payload.entityId) {
    const conv = await createOrGetSchemeConversation({
      bizType: payload.chatKind || 'customer',
      bizEntityId: String(payload.entityId),
      title: payload.title || '未命名会话',
      subtitle: payload.subtitle || '',
      senderUserId: payload.senderUserId || '',
      senderName: payload.senderName || '',
      receiverUserId: payload.receiverUserId || '',
      receiverName: payload.receiverName || '',
      sendType: payload.sendType || '',
      receiveType: payload.receiveType || ''
    })
    conversationId = conv && conv.id ? conv.id : ''
  }
  if (!conversationId) throw new Error('missing conversationId')
  const clientMsgId =
    payload.clientMsgId ||
    `${Date.now()}-${Math.random().toString(16).slice(2)}`
  const res = await axios.post(
    baseUrl + `/conversations/${conversationId}/messages`,
    {
      clientMsgId,
      msgType: payload.msgType || 'TEXT',
      content: payload.content || '',
      visibility: payload.visibility || 'PUBLIC',
      senderUserId: payload.senderUserId || '',
      senderName: payload.senderName || '',
      receiverUserId: payload.receiverUserId || '',
      receiverName: payload.receiverName || ''
    },
    schemeEcoJsonParseConfig({
      params: options && options.silent ? { loading: true } : undefined
    })
  )
  const cid = String(conversationId || '')
  return res && typeof res === 'object' ? { ...res, resolvedConversationId: cid } : res
}

export async function markSchemeConversationRead(conversationId, lastReadSeq) {
  if (!conversationId) return null
  return axios.post(baseUrl + `/conversations/${conversationId}/read`, {
    lastReadSeq: lastReadSeq || 0
  })
}

export async function bindSchemeDialogueSession(item, chatKind) {
  const receiverName =
    item?.receiverName ||
    item?.contactName ||
    item?.name ||
    item?.title ||
    item?.typeLabel ||
    '未命名会话'
  const receiverUserId =
    item?.receiverUserId ||
    item?.userId ||
    item?.contactId ||
    item?.targetUserId ||
    ''
  const title = receiverName
  const subtitle = [item?.company, item?.industry, item?.typeLabel].filter(Boolean).join(' · ')
  const conv = await createOrGetSchemeConversation({
    bizType: chatKind || 'customer',
    bizEntityId: String(item?.entityId || item?.id || ''),
    title,
    subtitle,
    receiverUserId: String(receiverUserId || ''),
    receiverName: receiverName,
    sendType: 'scheme',
    receiveType: 'company'
  })
  return normalizeSchemeDialogueSession(item, chatKind, conv)
}
